import React from "react";
import { Outlet } from "react-router-dom";
import { Box, Typography } from "@mui/material";

const TankControllerArt = () => (
  <svg
    viewBox="-65 30 640 530"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      width: "100%",
      maxWidth: "640px",
      maxHeight: "70vh",
      height: "auto",
      display: "block",
      margin: "0 auto",
    }}
    preserveAspectRatio="xMidYMid meet"
    aria-hidden="true"
  >
    <style>{`
      @keyframes waveBob {
        0%, 100% { transform: translateY(0); }
        50%      { transform: translateY(-3px); }
      }
      @keyframes wavePan {
        from { transform: translateX(0); }
        to   { transform: translateX(-40px); }
      }
      @keyframes waterRise {
        0%, 100% { transform: translateY(0); }
        50%      { transform: translateY(-6px); }
      }
      @keyframes ledPulse {
        0%, 100% { opacity: 0.25; transform: scale(1); }
        50%      { opacity: 0.85; transform: scale(1.5); }
      }
      @keyframes signalPing {
        0%   { transform: scale(0.4); opacity: 0.7; }
        70%  { opacity: 0.15; }
        100% { transform: scale(2.2); opacity: 0; }
      }
      @keyframes spin {
        from { transform: rotate(0deg); }
        to   { transform: rotate(360deg); }
      }
      @keyframes flow {
        from { stroke-dashoffset: 0; }
        to   { stroke-dashoffset: -24; }
      }
      @keyframes flowReverse {
        from { stroke-dashoffset: 0; }
        to   { stroke-dashoffset: 24; }
      }
      @keyframes float {
        0%, 100% { transform: translateY(0); opacity: 0.5; }
        50%      { transform: translateY(-6px); opacity: 0.9; }
      }
      @keyframes phoneTilt {
        0%, 100% { transform: rotate(-6deg); }
        50%      { transform: rotate(-2deg); }
      }
      @keyframes screenSweep {
        0%   { transform: translateX(-30px); opacity: 0; }
        50%  { opacity: 0.6; }
        100% { transform: translateX(30px); opacity: 0; }
      }

      .wave-pan { animation: wavePan 5s linear infinite; }
      .wave-pan-slow { animation: wavePan 8s linear infinite; }
      .wave-bob { animation: waveBob 4s ease-in-out infinite; transform-box: fill-box; transform-origin: center; }
      .water-fill { animation: waterRise 6s ease-in-out infinite; transform-box: fill-box; transform-origin: bottom; }
      .led-halo { animation: ledPulse 1.4s ease-in-out infinite; transform-box: fill-box; transform-origin: center; }
      .ping { animation: signalPing 2.4s ease-out infinite; transform-box: fill-box; transform-origin: center; }
      .ping-2 { animation: signalPing 2.4s ease-out 0.8s infinite; transform-box: fill-box; transform-origin: center; }
      .ping-3 { animation: signalPing 2.4s ease-out 1.6s infinite; transform-box: fill-box; transform-origin: center; }
      .pump-rotor {
        animation: spin 1.15s linear infinite;
        transform-box: fill-box;
        transform-origin: center;
      }
      .pipe-flow {
        stroke-dasharray: 6 6;
        animation: flow 1.4s linear infinite;
      }
      .pipe-flow-down {
        stroke-dasharray: 6 6;
        animation: flowReverse 1.4s linear infinite;
      }
      .float-dot { animation: float 3s ease-in-out infinite; transform-box: fill-box; transform-origin: center; }
      .float-dot-2 { animation: float 3.5s ease-in-out 0.5s infinite; transform-box: fill-box; transform-origin: center; }
      .float-dot-3 { animation: float 4s ease-in-out 1s infinite; transform-box: fill-box; transform-origin: center; }
      .phone-tilt { animation: phoneTilt 4s ease-in-out infinite; transform-box: fill-box; transform-origin: 50% 100%; }
      .data-ping { animation: signalPing 2.6s ease-out infinite; transform-box: fill-box; transform-origin: center; }
      .data-ping-2 { animation: signalPing 2.6s ease-out 1.3s infinite; transform-box: fill-box; transform-origin: center; }
      .screen-sweep { animation: screenSweep 2.4s ease-in-out infinite; }
      .data-flow {
        stroke-dasharray: 4 5;
        animation: flow 1.5s linear infinite;
      }
    `}</style>
    <defs>
      <linearGradient id="water" x1="0" y1="1" x2="0" y2="0">
        <stop offset="0%" stopColor="#0e7490" stopOpacity="0.95" />
        <stop offset="100%" stopColor="#22d3ee" stopOpacity="0.7" />
      </linearGradient>
      <linearGradient id="tankShell" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="rgba(255,255,255,0.18)" />
        <stop offset="50%" stopColor="rgba(255,255,255,0.06)" />
        <stop offset="100%" stopColor="rgba(255,255,255,0.18)" />
      </linearGradient>
      <linearGradient id="legShade" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="rgba(255,255,255,0.55)" />
        <stop offset="100%" stopColor="rgba(255,255,255,0.20)" />
      </linearGradient>
      <radialGradient id="signal" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="rgba(255,255,255,0.45)" />
        <stop offset="100%" stopColor="rgba(255,255,255,0)" />
      </radialGradient>
      <radialGradient id="sun" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="rgba(255,236,179,0.55)" />
        <stop offset="100%" stopColor="rgba(255,236,179,0)" />
      </radialGradient>
    </defs>

    {/* Sun */}
    <circle cx="460" cy="80" r="60" fill="url(#sun)" />
    <circle cx="460" cy="80" r="20" fill="rgba(255,236,179,0.6)" />

    {/* Distant ground line backdrop */}
    <path
      d="M-110 400 L520 400 L520 410 L-110 410 Z"
      fill="rgba(255,255,255,0.10)"
    />

    {/* Coconut palm silhouettes (between objects) */}
    <g fill="rgba(255,255,255,0.20)">
      {/* Left foreground palm */}
      <path d="M-100 400 L-100 362 Q-105 350 -114 348 Q-100 352 -98 360 Q-98 350 -92 342 Q-98 352 -94 362 Q-88 350 -78 350 Q-88 356 -94 364 L-96 400 Z" />
      {/* Right far-side palm (taller) */}
      <path d="M540 400 L540 320 Q535 310 526 308 Q540 312 542 320 Q542 310 548 302 Q542 312 546 322 Q552 310 562 310 Q552 316 546 324 Q558 314 568 320 L548 326 Q554 322 542 330 L538 400 Z" />
    </g>

    {/* Distant hill/horizon silhouette */}
    <path
      d="M-110 400 Q -50 380 60 395 Q 180 405 300 388 Q 420 372 540 392 L 540 410 L -110 410 Z"
      fill="rgba(255,255,255,0.07)"
    />

    {/* Distant birds (V-shapes) */}
    <g
      fill="none"
      stroke="rgba(255,255,255,0.45)"
      strokeWidth="1.5"
      strokeLinecap="round"
    >
      <path d="M 80 95 q 4 -4 8 0 q 4 -4 8 0" />
      <path d="M 110 110 q 3 -3 6 0 q 3 -3 6 0" />
      <path d="M 380 130 q 4 -4 8 0 q 4 -4 8 0" />
      <path d="M 410 115 q 3 -3 6 0 q 3 -3 6 0" />
    </g>

    {/* Extra floating particles spread out for ambience */}
    <g>
      <circle className="float-dot" cx="40" cy="130" r="2.2" fill="rgba(255,255,255,0.5)" />
      <circle className="float-dot-2" cx="120" cy="60" r="1.6" fill="rgba(255,255,255,0.45)" />
      <circle className="float-dot-3" cx="220" cy="90" r="1.8" fill="rgba(255,255,255,0.4)" />
      <circle className="float-dot" cx="320" cy="60" r="2" fill="rgba(255,255,255,0.5)" />
      <circle className="float-dot-2" cx="380" cy="100" r="1.6" fill="rgba(255,255,255,0.4)" />
      <circle className="float-dot-3" cx="510" cy="120" r="2.4" fill="rgba(255,255,255,0.5)" />
      <circle className="float-dot" cx="-60" cy="180" r="2" fill="rgba(255,255,255,0.4)" />
      <circle className="float-dot-2" cx="-50" cy="60" r="1.5" fill="rgba(255,255,255,0.4)" />
    </g>

    {/* === OVERHEAD TANK === */}
    {/* Top dome */}
    <ellipse cx="260" cy="150" rx="100" ry="20" fill="rgba(255,255,255,0.4)" stroke="rgba(255,255,255,0.7)" strokeWidth="2" />
    {/* Vent pipe on top */}
    <line x1="260" y1="135" x2="260" y2="118" stroke="rgba(255,255,255,0.7)" strokeWidth="2.5" strokeLinecap="round" />
    <path d="M256 118 q4 -6 8 0" stroke="rgba(255,255,255,0.7)" strokeWidth="2.5" fill="none" strokeLinecap="round" />

    {/* === SOLAR PANEL (small square, powers the water-level sensor) === */}
    <g transform="translate(322 96)">
      {/* Mounting pole anchored to tank dome */}
      <line x1="14" y1="42" x2="14" y2="56" stroke="rgba(255,255,255,0.7)" strokeWidth="1.5" />
      {/* Panel — compact square, tilted slightly */}
      <g transform="rotate(-15 14 26)">
        {/* Frame */}
        <rect x="-2" y="10" width="32" height="30" fill="#0f172a" stroke="rgba(255,255,255,0.9)" strokeWidth="1.2" rx="1.5" />
        {/* Solar cell grid — 3 × 3 */}
        <g stroke="rgba(56,189,248,0.7)" strokeWidth="0.5">
          <line x1="8"  y1="10" x2="8"  y2="40" />
          <line x1="18" y1="10" x2="18" y2="40" />
          <line x1="-2" y1="20" x2="30" y2="20" />
          <line x1="-2" y1="30" x2="30" y2="30" />
        </g>
        {/* Cell tint */}
        <rect x="-2" y="10" width="32" height="30" fill="url(#sun)" opacity="0.3" />
      </g>
    </g>
    {/* Cable from solar panel → water-level sensor on the tank wall */}
    <path
      d="M330 152 Q 360 200 360 240"
      stroke="rgba(255,255,255,0.55)"
      strokeWidth="1.5"
      strokeDasharray="2 3"
      fill="none"
      strokeLinecap="round"
    />
    {/* Water-level sensor probe on right side of tank */}
    <g transform="translate(355 240)">
      <rect x="0" y="-6" width="14" height="12" rx="2" fill="#155e75" stroke="rgba(255,255,255,0.85)" strokeWidth="1" />
      <circle cx="4" cy="0" r="1.4" fill="#22d3ee" />
      <circle className="led-halo" cx="4" cy="0" r="2.4" fill="#22d3ee" />
      {/* Sensor wire down into tank */}
      <line x1="10" y1="6" x2="10" y2="56" stroke="rgba(255,255,255,0.55)" strokeWidth="1" />
      <circle cx="10" cy="56" r="2" fill="#22d3ee" stroke="#ffffff" strokeWidth="1" />
    </g>

    {/* Tank cylinder body */}
    <rect x="160" y="150" width="200" height="160" fill="url(#tankShell)" stroke="rgba(255,255,255,0.65)" strokeWidth="2.5" />
    {/* Vertical seams */}
    <line x1="200" y1="150" x2="200" y2="310" stroke="rgba(255,255,255,0.18)" strokeWidth="1" />
    <line x1="260" y1="150" x2="260" y2="310" stroke="rgba(255,255,255,0.18)" strokeWidth="1" />
    <line x1="320" y1="150" x2="320" y2="310" stroke="rgba(255,255,255,0.18)" strokeWidth="1" />

    {/* Water inside (clipped) */}
    <clipPath id="tankClip">
      <rect x="160" y="150" width="200" height="160" />
    </clipPath>
    <g clipPath="url(#tankClip)">
      {/* 75% fill */}
      <rect className="water-fill" x="160" y="190" width="200" height="120" fill="url(#water)" />
      <path className="wave-pan" d="M120,190 Q150,178 180,190 T240,190 T300,190 T360,190 T420,190 L420,205 L120,205 Z" fill="rgba(255,255,255,0.22)" />
      <path className="wave-pan-slow" d="M120,198 Q160,188 200,198 T280,198 T360,198 T420,198 L420,215 L120,215 Z" fill="rgba(255,255,255,0.12)" />
    </g>

    {/* Bottom rim of tank */}
    <ellipse cx="260" cy="310" rx="100" ry="14" fill="rgba(255,255,255,0.20)" stroke="rgba(255,255,255,0.65)" strokeWidth="2" />

    {/* Level reading at center-bottom of tank */}
    <rect x="226" y="288" width="68" height="22" rx="4" fill="rgba(15,23,42,0.55)" stroke="rgba(255,255,255,0.55)" strokeWidth="1" />
    <circle cx="237" cy="299" r="3" fill="#22d3ee" stroke="#ffffff" strokeWidth="1" />
    <text x="248" y="304" fill="#ffffff" fontSize="14" fontFamily="Inter, sans-serif" fontWeight="800" letterSpacing="0.5">
      75%
    </text>

    {/* === LEGS / COLUMNS === */}
    <g stroke="rgba(255,255,255,0.75)" strokeWidth="2.5">
      {/* Front-left */}
      <line x1="186" y1="318" x2="180" y2="450" />
      <line x1="200" y1="318" x2="200" y2="450" />
      {/* Front-right */}
      <line x1="320" y1="318" x2="320" y2="450" />
      <line x1="334" y1="318" x2="340" y2="450" />
    </g>
    {/* Cross-bracing */}
    <g stroke="rgba(255,255,255,0.45)" strokeWidth="1.5">
      <line x1="186" y1="380" x2="200" y2="380" />
      <line x1="320" y1="380" x2="334" y2="380" />
      <line x1="200" y1="360" x2="320" y2="360" />
      <line x1="186" y1="420" x2="340" y2="420" />
      <line x1="200" y1="450" x2="320" y2="370" stroke="rgba(255,255,255,0.25)" />
      <line x1="320" y1="450" x2="200" y2="370" stroke="rgba(255,255,255,0.25)" />
    </g>

    {/* === SUCTION PIPE: drops deep into well water → up over rim → to pump === */}
    <path
      d="M-43 528 V 436 H 124 V 464"
      stroke="rgba(255,255,255,0.7)"
      strokeWidth="5"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      className="pipe-flow"
      d="M-43 528 V 436 H 124 V 464"
      stroke="#22d3ee"
      strokeWidth="3"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
      opacity="0.9"
    />
    {/* Foot valve / strainer dipped in deep well water */}
    <rect x="-47" y="526" width="8" height="4" fill="rgba(255,255,255,0.8)" />
    <line x1="-45" y1="528" x2="-41" y2="528" stroke="#0f766e" strokeWidth="1" />

    {/* === RISER PIPE: pump → up & over → enters tank top from the LEFT === */}
    <path
      d="M124 420 V 130 Q 124 118 136 118 L 188 118 Q 200 118 200 130 V 148"
      stroke="rgba(255,255,255,0.7)"
      strokeWidth="5"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      className="pipe-flow"
      d="M124 420 V 130 Q 124 118 136 118 L 188 118 Q 200 118 200 130 V 148"
      stroke="#22d3ee"
      strokeWidth="3"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
      opacity="0.9"
    />
    {/* Pressure gauge on riser */}
    <circle cx="135" cy="280" r="7" fill="rgba(255,255,255,0.95)" stroke="rgba(255,255,255,0.7)" strokeWidth="1" />
    <line x1="135" y1="280" x2="138" y2="276" stroke="#0f766e" strokeWidth="1.3" strokeLinecap="round" />
    {/* Joint flange where pipe drops into tank top (left side) */}
    <rect x="195" y="142" width="10" height="6" fill="rgba(255,255,255,0.9)" stroke="rgba(255,255,255,0.7)" strokeWidth="1" />

    {/* === OUTLET PIPE: tank → homes (right side) === */}
    <path
      d="M362 290 H 400 V 460 H 470"
      stroke="rgba(255,255,255,0.7)"
      strokeWidth="4"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      className="pipe-flow"
      d="M362 290 H 400 V 460 H 470"
      stroke="#22d3ee"
      strokeWidth="2.5"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
      opacity="0.9"
    />
    {/* Tap valve handle on outlet */}
    <circle cx="400" cy="370" r="6" fill="rgba(255,255,255,0.9)" />
    <line x1="394" y1="370" x2="406" y2="370" stroke="#0f766e" strokeWidth="2" />

    {/* === MOTOR PUMP — centrifugal pump on concrete pad === */}
    <g transform="translate(50 410)">
      <rect x="-2" y="50" width="112" height="9" rx="2" fill="rgba(255,255,255,0.88)" stroke="rgba(255,255,255,0.7)" strokeWidth="1" />
      <rect x="6" y="45" width="34" height="8" rx="2" fill="rgba(255,255,255,0.58)" />
      <rect x="66" y="45" width="34" height="8" rx="2" fill="rgba(255,255,255,0.58)" />
      <circle cx="12" cy="54.5" r="1.5" fill="#0f766e" />
      <circle cx="52" cy="54.5" r="1.5" fill="#0f766e" />
      <circle cx="96" cy="54.5" r="1.5" fill="#0f766e" />

      <rect x="0" y="18" width="57" height="28" rx="14" fill="#0f766e" stroke="rgba(255,255,255,0.86)" strokeWidth="1.5" />
      <ellipse cx="0" cy="32" rx="4" ry="14" fill="#0b5f58" stroke="rgba(255,255,255,0.55)" strokeWidth="0.9" />
      <ellipse cx="57" cy="32" rx="5" ry="14" fill="#0d9488" stroke="rgba(255,255,255,0.6)" strokeWidth="0.9" />
      <g stroke="rgba(255,255,255,0.42)" strokeWidth="1.2" strokeLinecap="round">
        <line x1="13" y1="21" x2="13" y2="43" />
        <line x1="22" y1="20" x2="22" y2="44" />
        <line x1="31" y1="20" x2="31" y2="44" />
        <line x1="40" y1="21" x2="40" y2="43" />
      </g>

      <rect x="18" y="5" width="24" height="14" rx="2.5" fill="#155e75" stroke="rgba(255,255,255,0.72)" strokeWidth="1" />
      <circle cx="24" cy="12" r="1.7" fill="#10b981" />
      <circle className="led-halo" cx="24" cy="12" r="3.2" fill="#10b981" />
      <rect x="30" y="10" width="8" height="1.5" rx="0.75" fill="#ffffff" opacity="0.62" />
      <rect x="30" y="14" width="5" height="1.5" rx="0.75" fill="#ffffff" opacity="0.42" />

      <rect x="56" y="24" width="11" height="16" rx="2" fill="rgba(255,255,255,0.68)" stroke="rgba(255,255,255,0.7)" strokeWidth="1" />
      <line x1="62" y1="32" x2="70" y2="32" stroke="#0f766e" strokeWidth="2.4" strokeLinecap="round" />

      <rect x="80" y="7" width="11" height="9" rx="1.5" fill="rgba(255,255,255,0.9)" stroke="rgba(255,255,255,0.75)" strokeWidth="1" />
      <rect x="80" y="52" width="11" height="9" rx="1.5" fill="rgba(255,255,255,0.9)" stroke="rgba(255,255,255,0.75)" strokeWidth="1" />
      <circle cx="86" cy="34" r="25" fill="#e6fffb" stroke="rgba(255,255,255,0.9)" strokeWidth="2" />
      <circle cx="86" cy="34" r="21" fill="#ccfbf1" stroke="rgba(15,118,110,0.68)" strokeWidth="2" />
      <circle cx="86" cy="34" r="14.5" fill="rgba(255,255,255,0.5)" stroke="rgba(15,118,110,0.34)" strokeWidth="1" />
      <g transform="translate(86 34)">
        <g>
          <animateTransform
            attributeName="transform"
            type="rotate"
            from="0"
            to="360"
            dur="1.1s"
            repeatCount="indefinite"
          />
          {[
            { rotate: 0, fill: "#0f766e" },
            { rotate: 90, fill: "#0891b2" },
            { rotate: 180, fill: "#0d9488" },
            { rotate: 270, fill: "#14b8a6" },
          ].map((blade) => (
            <path
              key={blade.rotate}
              d="M0 -3 C8 -12 15 -8 12 -1 C7 -3 3 -2 0 0 Z"
              fill={blade.fill}
              transform={`rotate(${blade.rotate})`}
            />
          ))}
          <circle cx="8" cy="-8" r="1.6" fill="#ffffff" opacity="0.85" />
        </g>
        <circle r="5" fill="#ffffff" stroke="#0f766e" strokeWidth="1.6" />
      </g>
      <circle cx="86" cy="34" r="25" fill="none" stroke="rgba(15,118,110,0.74)" strokeWidth="2" />
    </g>

    {/* === IoT controller mounted on tank wall === */}
    <g transform="translate(170 195)">
      {/* Pinging signal rings (3 staggered) */}
      <circle className="ping" cx="40" cy="-22" r="14" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="2" />
      <circle className="ping-2" cx="40" cy="-22" r="14" fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="2" />
      <circle className="ping-3" cx="40" cy="-22" r="14" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="2" />
      {/* Soft halo */}
      <circle cx="40" cy="28" r="55" fill="url(#signal)" />
      {/* Subtle drop-shadow */}
      <rect x="-2" y="-2" width="84" height="60" rx="7" fill="rgba(0,0,0,0.18)" />
      {/* Controller box */}
      <rect x="0" y="0" width="80" height="56" rx="6" fill="#ffffff" stroke="rgba(255,255,255,0.85)" strokeWidth="1.5" />
      {/* Power LED top-left */}
      <circle cx="10" cy="9" r="2.5" fill="#10b981" />
      <circle className="led-halo" cx="10" cy="9" r="4" fill="#10b981" />
      {/* Status text dashes */}
      <rect x="18" y="7" width="22" height="2" rx="1" fill="#94a3b8" />
      <rect x="18" y="11" width="14" height="2" rx="1" fill="#94a3b8" />
      {/* Network signal bars (top-right) */}
      <g transform="translate(60 6)">
        <rect x="0" y="6" width="2" height="3" fill="#10b981" />
        <rect x="3" y="4" width="2" height="5" fill="#10b981" />
        <rect x="6" y="2" width="2" height="7" fill="#10b981" />
        <rect x="9" y="0" width="2" height="9" fill="#10b981" />
      </g>
      {/* Brand "J" badge centered on display */}
      <circle cx="40" cy="34" r="14" fill="#0d9488" stroke="#ffffff" strokeWidth="1.5" />
      <text
        x="40"
        y="40"
        textAnchor="middle"
        fill="#ffffff"
        fontSize="18"
        fontWeight="800"
        fontFamily="Inter, sans-serif"
      >
        J
      </text>
      {/* Antenna */}
      <line x1="40" y1="0" x2="40" y2="-20" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" />
      <circle cx="40" cy="-22" r="3.5" fill="#ffffff" />
    </g>

    {/* Ground line */}
    <line x1="-110" y1="465" x2="520" y2="465" stroke="rgba(255,255,255,0.55)" strokeWidth="2" />
    {/* Subtle ground texture */}
    <g stroke="rgba(255,255,255,0.18)" strokeWidth="1">
      <line x1="-90" y1="475" x2="-65" y2="475" />
      <line x1="-40" y1="478" x2="-15" y2="478" />
      <line x1="160" y1="476" x2="190" y2="476" />
      <line x1="240" y1="478" x2="280" y2="478" />
      <line x1="350" y1="476" x2="380" y2="476" />
      <line x1="500" y1="478" x2="515" y2="478" />
    </g>

    {/* === DUG WELL — stone parapet above ground + cross-section shaft below === */}
    <g transform="translate(-35 0)">
      {/* Underground shaft cross-section (visible below ground line at y=465) */}
      <path
        d="M-50 465 L-50 535 Q -50 545 -36 545 L 20 545 Q 34 545 34 535 L 34 465 Z"
        fill="rgba(0,0,0,0.40)"
        stroke="rgba(255,255,255,0.45)"
        strokeWidth="1.5"
      />
      {/* Stone-course lines on the underground shaft */}
      <g stroke="rgba(255,255,255,0.35)" strokeWidth="0.8">
        <line x1="-50" y1="485" x2="34" y2="485" />
        <line x1="-50" y1="505" x2="34" y2="505" />
        <line x1="-50" y1="525" x2="34" y2="525" />
        <line x1="-22" y1="465" x2="-22" y2="485" />
        <line x1="6" y1="485" x2="6" y2="505" />
        <line x1="-12" y1="505" x2="-12" y2="525" />
        <line x1="14" y1="525" x2="14" y2="545" />
      </g>
      {/* Water deep inside the well shaft */}
      <ellipse cx="-8" cy="525" rx="38" ry="6" fill="url(#water)" />
      <ellipse className="wave-bob" cx="-8" cy="523" rx="24" ry="1.6" fill="rgba(255,255,255,0.55)" />
      <ellipse className="wave-bob" cx="-8" cy="526" rx="14" ry="1" fill="rgba(255,255,255,0.30)" style={{ animationDelay: "0.5s" }} />

      {/* Above-ground stone parapet (front face) */}
      <path
        d="M-58 465 L-58 447 Q -58 444 -50 444 L 34 444 Q 42 444 42 447 L 42 465 Z"
        fill="rgba(255,255,255,0.32)"
        stroke="rgba(255,255,255,0.7)"
        strokeWidth="2"
      />
      {/* Stone block texture on parapet */}
      <g stroke="rgba(255,255,255,0.5)" strokeWidth="1">
        <line x1="-58" y1="455" x2="42" y2="455" />
        <line x1="-30" y1="447" x2="-30" y2="455" />
        <line x1="-8" y1="455" x2="-8" y2="465" />
        <line x1="20" y1="447" x2="20" y2="455" />
        <line x1="14" y1="455" x2="14" y2="465" />
      </g>
      {/* Top rim (looking slightly down into the well) */}
      <ellipse
        cx="-8"
        cy="447"
        rx="50"
        ry="10"
        fill="rgba(255,255,255,0.18)"
        stroke="rgba(255,255,255,0.75)"
        strokeWidth="2.5"
      />
      {/* Dark inner opening (depth into shaft) */}
      <ellipse
        cx="-8"
        cy="450"
        rx="42"
        ry="7"
        fill="#0a2e2c"
        stroke="rgba(255,255,255,0.5)"
        strokeWidth="1.2"
      />
    </g>

    {/* === HOMES (right side) === */}
    <g transform="translate(440 0)">
      {/* House 1 */}
      <g>
        <polygon points="0,440 18,420 36,440" fill="rgba(255,255,255,0.45)" />
        <rect x="3" y="440" width="30" height="25" fill="rgba(255,255,255,0.85)" stroke="rgba(255,255,255,0.6)" strokeWidth="1" />
        <rect x="14" y="450" width="8" height="15" fill="#0f766e" />
        <rect x="6" y="445" width="5" height="5" fill="#0891b2" opacity="0.7" />
        <rect x="25" y="445" width="5" height="5" fill="#0891b2" opacity="0.7" />
      </g>
      {/* House 2 - taller */}
      <g transform="translate(42 -8)">
        <polygon points="0,448 22,422 44,448" fill="rgba(255,255,255,0.5)" />
        <rect x="3" y="448" width="38" height="25" fill="rgba(255,255,255,0.92)" stroke="rgba(255,255,255,0.6)" strokeWidth="1" />
        <rect x="17" y="455" width="10" height="18" fill="#0f766e" />
        <rect x="6" y="453" width="6" height="5" fill="#0891b2" opacity="0.7" />
        <rect x="32" y="453" width="6" height="5" fill="#0891b2" opacity="0.7" />
        {/* roof antenna */}
        <line x1="22" y1="422" x2="22" y2="412" stroke="rgba(255,255,255,0.7)" strokeWidth="1.5" strokeLinecap="round" />
        <circle cx="22" cy="411" r="1.5" fill="rgba(255,255,255,0.85)" />
      </g>
      {/* House 3 */}
      <g transform="translate(86 0)">
        <polygon points="0,440 16,422 32,440" fill="rgba(255,255,255,0.4)" />
        <rect x="2" y="440" width="28" height="25" fill="rgba(255,255,255,0.8)" stroke="rgba(255,255,255,0.6)" strokeWidth="1" />
        <rect x="12" y="450" width="8" height="15" fill="#0f766e" />
        <rect x="5" y="445" width="4" height="5" fill="#0891b2" opacity="0.7" />
        <rect x="23" y="445" width="4" height="5" fill="#0891b2" opacity="0.7" />
      </g>
    </g>

    {/* === Modern mobile user === */}
    <g transform="translate(226 380)">
      <path d="M53 16 Q65 25 53 35" fill="none" stroke="rgba(255,255,255,0.42)" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M59 10 Q77 25 59 41" fill="none" stroke="rgba(255,255,255,0.24)" strokeWidth="1.6" strokeLinecap="round" />

      <ellipse cx="24" cy="88" rx="25" ry="4" fill="rgba(0,0,0,0.16)" />
      <circle cx="22" cy="13" r="9" fill="rgba(255,255,255,0.94)" stroke="rgba(255,255,255,0.85)" strokeWidth="1" />
      <path d="M12 12 Q14 3 23 4 Q31 5 32 13 Q26 9 20 9 Q16 10 12 12 Z" fill="#1f2937" opacity="0.9" />

      <path
        d="M8 29 Q22 22 37 29 L35 58 Q22 64 9 58 Z"
        fill="#0f766e"
        stroke="rgba(255,255,255,0.72)"
        strokeWidth="1"
      />
      <path d="M13 33 Q9 45 13 55" stroke="rgba(255,255,255,0.86)" strokeWidth="4" strokeLinecap="round" fill="none" />
      <path d="M35 33 Q43 38 47 47" stroke="rgba(255,255,255,0.88)" strokeWidth="4" strokeLinecap="round" fill="none" />
      <path d="M20 31 Q31 42 43 48" stroke="rgba(255,255,255,0.62)" strokeWidth="3" strokeLinecap="round" fill="none" />

      <g transform="translate(39 38) rotate(-8)">
        <rect x="0" y="0" width="24" height="38" rx="5" fill="#0f172a" stroke="rgba(255,255,255,0.88)" strokeWidth="1.2" />
        <rect x="3" y="5" width="18" height="28" rx="2.5" fill="#083344" />
        <rect className="screen-sweep" x="3" y="5" width="5" height="28" fill="rgba(255,255,255,0.14)" />
        <rect x="6" y="8" width="12" height="3" rx="1.5" fill="#0f766e" />
        <text x="12" y="23" textAnchor="middle" fill="#22d3ee" fontSize="8.5" fontFamily="Inter, sans-serif" fontWeight="800">
          75%
        </text>
        <path d="M7 27 H17" stroke="#10b981" strokeWidth="1.6" strokeLinecap="round" />
        <circle cx="12" cy="35" r="1.2" fill="rgba(255,255,255,0.72)" />
      </g>

      <path d="M14 58 L12 86" stroke="#1f2937" strokeWidth="5.8" strokeLinecap="round" />
      <path d="M31 58 L35 86" stroke="#1f2937" strokeWidth="5.8" strokeLinecap="round" />
      <ellipse cx="11" cy="87" rx="5.5" ry="2.2" fill="#0f172a" />
      <ellipse cx="36" cy="87" rx="5.5" ry="2.2" fill="#0f172a" />
    </g>


    {/* Floating data dots */}
    <circle className="float-dot" cx="80" cy="200" r="2.5" fill="rgba(255,255,255,0.5)" />
    <circle className="float-dot-2" cx="60" cy="280" r="2" fill="rgba(255,255,255,0.4)" />
    <circle className="float-dot-3" cx="470" cy="180" r="2.5" fill="rgba(255,255,255,0.5)" />
    <circle className="float-dot" cx="490" cy="270" r="2" fill="rgba(255,255,255,0.4)" />
  </svg>
);

export default function AuthLayout() {
  return (
    <Box
      sx={{
        height: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
        display: "flex",
        backgroundColor: "background.default",
      }}
    >
      {/* Left brand panel (desktop only) */}
      <Box
        sx={{
          flex: { xs: 0, md: 1 },
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          p: { md: 3, lg: 4 },
          minWidth: 0,
          maxHeight: "100vh",
          overflow: "hidden",
          background:
            "radial-gradient(circle at 20% 0%, rgba(34,211,238,0.35) 0%, rgba(13,148,136,0) 55%), linear-gradient(135deg, #0f766e 0%, #0d9488 55%, #0891b2 100%)",
          color: "#fff",
          position: "relative",
        }}
      >
        {/* decorative blobs */}
        <Box
          sx={{
            position: "absolute",
            width: 320,
            height: 320,
            top: -80,
            right: -80,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0) 70%)",
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            width: 360,
            height: 360,
            bottom: -120,
            left: -100,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0) 65%)",
            pointerEvents: "none",
          }}
        />

        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            maxWidth: 680,
            maxHeight: "100%",
            mx: "auto",
          }}
        >
          <TankControllerArt />
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "1.5rem",
              letterSpacing: "-0.005em",
              color: "#fff",
              mt: 3,
              textAlign: "center",
            }}
          >
            Jothi Engineering
          </Typography>
          <Typography
            sx={{
              color: "rgba(255,255,255,0.78)",
              fontSize: "0.875rem",
              mt: 0.5,
              textAlign: "center",
            }}
          >
            Water Management System
          </Typography>
        </Box>
      </Box>

      {/* Right panel — login form */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "background.paper",
          px: 3,
          py: { xs: 4, md: 5 },
          position: "relative",
          minWidth: 0,
          maxHeight: "100vh",
          overflow: "hidden",
          // Animated keyframes scoped to this panel
          "@keyframes floatBlob": {
            "0%, 100%": { transform: "translate(0, 0) scale(1)" },
            "33%": { transform: "translate(30px, -40px) scale(1.05)" },
            "66%": { transform: "translate(-20px, 25px) scale(0.95)" },
          },
          "@keyframes floatBlob2": {
            "0%, 100%": { transform: "translate(0, 0) scale(1)" },
            "50%": { transform: "translate(-35px, 20px) scale(1.08)" },
          },
          "@keyframes floatBlob3": {
            "0%, 100%": { transform: "translate(0, 0) scale(1)" },
            "50%": { transform: "translate(40px, -25px) scale(0.92)" },
          },
          "@keyframes bubbleFall": {
            "0%": { transform: "translateY(0) scale(0.85)", opacity: 0 },
            "10%": { opacity: 0.7 },
            "90%": { opacity: 0.7 },
            "100%": { transform: "translateY(115vh) scale(1.05)", opacity: 0 },
          },
          "@keyframes meshShift": {
            "0%, 100%": { backgroundPosition: "0% 0%, 100% 100%" },
            "50%": { backgroundPosition: "100% 0%, 0% 100%" },
          },
          "@keyframes waveFlow": {
            "0%": { transform: "translateX(0)" },
            "100%": { transform: "translateX(-50%)" },
          },
          "@keyframes waveFlowReverse": {
            "0%": { transform: "translateX(-50%)" },
            "100%": { transform: "translateX(0)" },
          },
          "@keyframes ripple": {
            "0%": { transform: "scale(0.2)", opacity: 0.7 },
            "100%": { transform: "scale(2.4)", opacity: 0 },
          },
        }}
      >
        {/* Animated gradient mesh background */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "radial-gradient(circle at 20% 20%, rgba(13,148,136,0.10) 0%, rgba(13,148,136,0) 45%), " +
              "radial-gradient(circle at 80% 80%, rgba(34,211,238,0.10) 0%, rgba(34,211,238,0) 45%)",
            backgroundSize: "200% 200%",
            animation: "meshShift 18s ease-in-out infinite",
            pointerEvents: "none",
          }}
        />

        {/* Floating teal blobs */}
        <Box
          sx={{
            position: "absolute",
            width: 220,
            height: 220,
            top: "8%",
            right: "-60px",
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(13,148,136,0.18) 0%, rgba(13,148,136,0) 70%)",
            filter: "blur(2px)",
            animation: "floatBlob 14s ease-in-out infinite",
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            width: 280,
            height: 280,
            bottom: "5%",
            left: "-80px",
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(34,211,238,0.16) 0%, rgba(34,211,238,0) 70%)",
            filter: "blur(2px)",
            animation: "floatBlob2 18s ease-in-out infinite",
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            width: 160,
            height: 160,
            top: "55%",
            right: "12%",
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(20,184,166,0.14) 0%, rgba(20,184,166,0) 70%)",
            filter: "blur(2px)",
            animation: "floatBlob3 12s ease-in-out infinite",
            pointerEvents: "none",
          }}
        />

        {/* Falling water drops — fall from the top of the panel */}
        {[
          { left: "10%", size: 20, dur: 6,   delay: 0 },
          { left: "14%", size: 26, dur: 5,   delay: 1.8 },
          { left: "18%", size: 18, dur: 6.5, delay: 3.2 },
          { left: "22%", size: 22, dur: 5.5, delay: 0.6 },
          { left: "30%", size: 28, dur: 6,   delay: 2.4 },
          { left: "34%", size: 20, dur: 5,   delay: 0.4 },
          { left: "38%", size: 24, dur: 6.5, delay: 3.6 },
          { left: "42%", size: 18, dur: 5.5, delay: 1.0 },
          { left: "54%", size: 26, dur: 6,   delay: 1.4 },
          { left: "58%", size: 22, dur: 5.5, delay: 3.0 },
          { left: "62%", size: 28, dur: 6.5, delay: 0.2 },
          { left: "66%", size: 20, dur: 5,   delay: 2.0 },
          { left: "78%", size: 24, dur: 6,   delay: 0.8 },
          { left: "82%", size: 18, dur: 5,   delay: 2.6 },
          { left: "86%", size: 26, dur: 6.5, delay: 1.2 },
          { left: "90%", size: 22, dur: 5.5, delay: 3.4 },
        ].map((b, i) => (
          <Box
            key={i}
            sx={{
              position: "absolute",
              top: -40,
              left: b.left,
              width: b.size,
              height: b.size * 1.4,
              animation: `bubbleFall ${b.dur}s linear ${b.delay}s infinite`,
              pointerEvents: "none",
              filter: "drop-shadow(0 2px 4px rgba(13,148,136,0.18))",
              zIndex: 1,
            }}
          >
            <svg viewBox="0 0 24 36" width="100%" height="100%" aria-hidden>
              <defs>
                <radialGradient
                  id={`drop-grad-${i}`}
                  cx="35%"
                  cy="35%"
                  r="65%"
                >
                  <stop offset="0%" stopColor="rgba(255,255,255,0.95)" />
                  <stop offset="55%" stopColor="rgba(34,211,238,0.55)" />
                  <stop offset="100%" stopColor="rgba(13,148,136,0.30)" />
                </radialGradient>
              </defs>
              {/* Classic teardrop — pointed at top, rounded at bottom */}
              <path
                d="M12 0 C 4 12 0 22 0 27 C 0 32 5 36 12 36 C 19 36 24 32 24 27 C 24 22 20 12 12 0 Z"
                fill={`url(#drop-grad-${i})`}
                stroke="rgba(34,211,238,0.45)"
                strokeWidth="0.6"
              />
              {/* Inner highlight (white shine, top-left) */}
              <ellipse
                cx="9"
                cy="14"
                rx="3"
                ry="5"
                fill="rgba(255,255,255,0.55)"
              />
            </svg>
          </Box>
        ))}

        {/* Water pool at the bottom — body of water for drops to "fall into" */}
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: 180,
            pointerEvents: "none",
            zIndex: 0,
          }}
        >
          {/* Deep water gradient base */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(180deg, rgba(34,211,238,0) 0%, rgba(34,211,238,0.15) 35%, rgba(13,148,136,0.32) 75%, rgba(15,118,110,0.45) 100%)",
            }}
          />

          {/* Back wave — slow, deep teal */}
          <Box
            component="svg"
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              width: "200%",
              height: 110,
              animation: "waveFlow 22s linear infinite",
            }}
          >
            <path
              d="M0,60 C150,100 350,20 600,60 C850,100 1050,20 1200,60 L1200,120 L0,120 Z"
              fill="rgba(15,118,110,0.45)"
            />
          </Box>

          {/* Mid wave — lighter cyan, opposite direction */}
          <Box
            component="svg"
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              width: "200%",
              height: 90,
              animation: "waveFlowReverse 16s linear infinite",
            }}
          >
            <path
              d="M0,50 C200,20 400,80 600,50 C800,20 1000,80 1200,50 L1200,120 L0,120 Z"
              fill="rgba(34,211,238,0.35)"
            />
          </Box>

          {/* Front wave — brightest crest, fastest */}
          <Box
            component="svg"
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              width: "200%",
              height: 70,
              animation: "waveFlow 11s linear infinite",
            }}
          >
            <path
              d="M0,40 C220,80 440,10 600,40 C760,70 980,10 1200,40 L1200,120 L0,120 Z"
              fill="rgba(255,255,255,0.40)"
            />
          </Box>

          {/* Surface highlight line — thin shimmer right at the water line */}
          <Box
            component="svg"
            viewBox="0 0 1200 8"
            preserveAspectRatio="none"
            sx={{
              position: "absolute",
              bottom: 60,
              left: 0,
              width: "200%",
              height: 6,
              animation: "waveFlow 14s linear infinite",
              opacity: 0.6,
            }}
          >
            <path
              d="M0,4 C200,2 400,6 600,4 C800,2 1000,6 1200,4"
              stroke="rgba(255,255,255,0.85)"
              strokeWidth="1"
              fill="none"
            />
          </Box>

          {/* Splash ripples where drops land — staggered around the surface */}
          {[
            { left: "12%", size: 26, dur: 2.4, delay: 0.2 },
            { left: "32%", size: 32, dur: 2.8, delay: 1.4 },
            { left: "48%", size: 22, dur: 2.2, delay: 0.8 },
            { left: "62%", size: 30, dur: 2.6, delay: 2.1 },
            { left: "78%", size: 24, dur: 2.0, delay: 1.0 },
            { left: "90%", size: 28, dur: 2.5, delay: 1.7 },
          ].map((r, i) => (
            <Box
              key={`ripple-${i}`}
              sx={{
                position: "absolute",
                bottom: 56,
                left: r.left,
                width: r.size,
                height: r.size / 3,
                borderRadius: "50%",
                border: "1.5px solid rgba(255,255,255,0.7)",
                animation: `ripple ${r.dur}s ease-out ${r.delay}s infinite`,
                pointerEvents: "none",
              }}
            />
          ))}
        </Box>

        {/* Login form sits on top, with welcome header + footer flanking it */}
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 3,
            width: "100%",
            maxWidth: 500,
          }}
        >
          {/* Welcome heading above the card */}
          <Box sx={{ textAlign: "center" }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "1.5rem",
                lineHeight: 1.15,
                color: "primary.main",
                letterSpacing: "-0.005em",
              }}
            >
              Jothi Engineering
            </Typography>
            <Typography
              sx={{
                color: "text.secondary",
                fontSize: "0.875rem",
                mt: 0.5,
              }}
            >
              Sign in to continue to your admin console
            </Typography>
          </Box>

          <Outlet />

          {/* Footer note below the card */}
          <Box sx={{ textAlign: "center" }}>
            <Typography
              sx={{
                color: "text.disabled",
                fontSize: "0.6875rem",
                mt: 0.25,
              }}
            >
              © {new Date().getFullYear()} Jothi Engineering
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
